import React, { useEffect, useMemo, useState } from "react";
import AdminLayout from "../../components/layout/AdminLayout";
import AdminSelect from "../../components/AdminSelect";
import { adminApi } from "../../api";
import "./AdminIntegrationTests.css";

export default function AdminIntegrationTests() {
  const [venues, setVenues]   = useState([]);
  const [tests, setTests]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  const [selectedVenueId, setSelectedVenueId] = useState("");
  const [activeTestId, setActiveTestId]       = useState(null);
  const [inputs, setInputs]                   = useState({});   // { [testId]: { field: value } }
  const [running, setRunning]                 = useState({});   // { [testId]: bool }
  const [results, setResults]                 = useState({});   // { [testId]: result }
  const [confirmingId, setConfirmingId]       = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [venuesData, testsData] = await Promise.all([
          adminApi.getVenues(),
          adminApi.listIntegrationTests(),
        ]);
        setVenues(venuesData);
        setTests(testsData.tests || []);
        if (venuesData[0]) setSelectedVenueId(String(venuesData[0].id));
      } catch (err) {
        setError(err.message);
      }
      setLoading(false);
    })();
  }, []);

  const selectedVenue = useMemo(
    () => venues.find(v => String(v.id) === String(selectedVenueId)),
    [venues, selectedVenueId]
  );
  const selectedProvider = selectedVenue?.provider_type || "s7000";
  const visibleTests = useMemo(
    () => tests.filter(t => t.provider === selectedProvider),
    [tests, selectedProvider]
  );

  // Auto-select first test when venue changes
  useEffect(() => {
    if (!visibleTests.find(t => t.id === activeTestId)) {
      setActiveTestId(visibleTests[0]?.id || null);
    }
  }, [visibleTests, activeTestId]);

  const activeTest = visibleTests.find(t => t.id === activeTestId) || null;

  const stats = useMemo(() => {
    let pass = 0, fail = 0, total = 0;
    for (const t of visibleTests) {
      const r = results[t.id];
      if (!r) continue;
      total += 1;
      if (r.ok) pass += 1; else fail += 1;
    }
    return { pass, fail, total, remaining: visibleTests.length - total };
  }, [visibleTests, results]);

  function setInput(testId, field, value) {
    setInputs(prev => ({ ...prev, [testId]: { ...(prev[testId] || {}), [field]: value } }));
  }

  function statusFor(t) {
    if (running[t.id]) return "running";
    const r = results[t.id];
    if (!r) return "idle";
    return r.ok ? "ok" : "fail";
  }

  async function runTest(test, { skipConfirm = false } = {}) {
    if (test.destructive && !skipConfirm && confirmingId !== test.id) {
      setConfirmingId(test.id);
      return;
    }
    setConfirmingId(null);

    for (const inp of (test.requiresInput || [])) {
      if (inp.required && !(inputs[test.id]?.[inp.name] || "").trim()) {
        setResults(prev => ({ ...prev, [test.id]: { ok: false, error: `${inp.label} is required` } }));
        return;
      }
    }

    setRunning(prev => ({ ...prev, [test.id]: true }));
    setResults(prev => ({ ...prev, [test.id]: null }));
    try {
      const result = await adminApi.runIntegrationTest(
        Number(selectedVenueId),
        test.id,
        inputs[test.id] || {}
      );
      setResults(prev => ({ ...prev, [test.id]: result }));
    } catch (err) {
      setResults(prev => ({ ...prev, [test.id]: { ok: false, error: err.message } }));
    }
    setRunning(prev => ({ ...prev, [test.id]: false }));
  }

  async function runAllSafe() {
    const safe = visibleTests.filter(t => !t.destructive && !(t.requiresInput || []).some(i => i.required));
    for (const t of safe) {
      // eslint-disable-next-line no-await-in-loop
      await runTest(t);
    }
  }

  const venueOptions = venues.map(v => ({
    value: String(v.id),
    label: `${v.name} · ${(v.provider_type || "s7000").toUpperCase()}`,
  }));

  return (
    <AdminLayout>
      <div className="it-page">
        <div className="page-header">
          <div>
            <h1>Integration Tests</h1>
            <p className="page-subtitle">Live connectivity and smoke tests against S7000 and IGT sandboxes.</p>
          </div>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        {loading ? (
          <div className="loading-state">Loading…</div>
        ) : (
          <>
            {/* Toolbar */}
            <div className="it-bar">
              <span className="it-bar-label">Venue</span>
              <div className="it-bar-venue">
                <AdminSelect
                  value={selectedVenueId}
                  onChange={setSelectedVenueId}
                  options={venueOptions}
                  placeholder="Select a venue…"
                  searchable
                />
              </div>
              <div className="it-bar-spacer" />
              <div className="it-bar-stats">
                <span><span className="it-dot ok"   /> {stats.pass} pass</span>
                <span style={{ opacity: 0.4 }}>·</span>
                <span><span className="it-dot fail" /> {stats.fail} fail</span>
                <span style={{ opacity: 0.4 }}>·</span>
                <span><span className="it-dot idle" /> {stats.remaining} pending</span>
              </div>
              <button
                className="btn btn-primary"
                onClick={runAllSafe}
                disabled={!selectedVenueId || visibleTests.length === 0}
              >
                Run all safe
              </button>
            </div>

            {/* Split layout */}
            <div className="it-split">
              {/* Navigator */}
              <aside className="it-nav">
                <div className="it-nav-head">
                  <span className="it-nav-title">Tests</span>
                  <span className="it-nav-count">{visibleTests.length}</span>
                </div>
                {visibleTests.length === 0 ? (
                  <div className="it-panel-empty" style={{ padding: "2rem 1rem" }}>
                    No tests for {selectedProvider.toUpperCase()}
                  </div>
                ) : (
                  <ul className="it-nav-list">
                    {visibleTests.map(t => {
                      const st = statusFor(t);
                      const r = results[t.id];
                      return (
                        <li key={t.id}>
                          <button
                            className={`it-nav-item ${activeTestId === t.id ? "active" : ""}`}
                            onClick={() => setActiveTestId(t.id)}
                            type="button"
                          >
                            <span className={`it-dot ${st}`} />
                            <span className="it-nav-item-name">{stripPrefix(t.name, t.provider)}</span>
                            <span className="it-nav-item-meta">
                              {r?.duration_ms != null ? `${r.duration_ms}ms` : ""}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </aside>

              {/* Detail */}
              <section className="it-panel">
                {!activeTest ? (
                  <div className="it-panel-empty">
                    <div className="it-panel-empty-icon">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="4 17 10 11 4 5" />
                        <line x1="12" y1="19" x2="20" y2="19" />
                      </svg>
                    </div>
                    Select a test from the list to run it.
                  </div>
                ) : (
                  <TestDetail
                    test={activeTest}
                    venueId={selectedVenueId}
                    isRunning={!!running[activeTest.id]}
                    result={results[activeTest.id]}
                    confirming={confirmingId === activeTest.id}
                    values={inputs[activeTest.id] || {}}
                    onChange={(f, v) => setInput(activeTest.id, f, v)}
                    onRun={() => runTest(activeTest)}
                    onConfirm={() => runTest(activeTest, { skipConfirm: true })}
                    onCancel={() => setConfirmingId(null)}
                  />
                )}
              </section>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}

function stripPrefix(name, provider) {
  const p = provider.toUpperCase();
  return name.replace(new RegExp(`^${p}\\s*[—-]\\s*`, "i"), "");
}

/* ═══ Detail panel ═══════════════════════════════════════ */
function TestDetail({ test, isRunning, result, confirming, values, onChange, onRun, onConfirm, onCancel, venueId }) {
  const hasResult = !!result;
  const [preview, setPreview] = useState(null);
  const [previewErr, setPreviewErr] = useState("");

  // Fetch preview whenever test / venue / inputs change
  useEffect(() => {
    let cancelled = false;
    if (!venueId || !test?.id) return;
    const timer = setTimeout(async () => {
      try {
        const p = await adminApi.previewIntegrationTest(Number(venueId), test.id, values);
        if (!cancelled) {
          setPreview(p);
          setPreviewErr(p?.error || "");
        }
      } catch (err) {
        if (!cancelled) {
          setPreview(null);
          setPreviewErr(err.message);
        }
      }
    }, 120);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [venueId, test?.id, values]);

  return (
    <>
      <header className="it-detail-head">
        <div className="it-detail-tags">
          <span className={`it-tag ${test.provider}`}>{test.provider}</span>
          {test.destructive && <span className="it-tag destructive">destructive</span>}
        </div>
        <h2 className="it-detail-title">{test.name}</h2>
        <p className="it-detail-desc">{test.description}</p>
      </header>

      <div className="it-detail-body">
        {(test.requiresInput || []).length > 0 && (
          <div className="it-form">
            {test.requiresInput.map(inp => (
              <div key={inp.name}>
                <label>{inp.label}{inp.required && " *"}</label>
                <input
                  type="text"
                  value={values[inp.name] || ""}
                  onChange={e => onChange(inp.name, e.target.value)}
                  placeholder={inp.placeholder || inp.label}
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>
            ))}
          </div>
        )}

        {/* Request preview — ALWAYS visible */}
        <RequestPreview preview={preview} error={previewErr} />

        {confirming && (
          <div className="it-confirm">
            This test will create or modify real data in the sandbox.&nbsp;
            <strong>Confirm to proceed.</strong>
          </div>
        )}

        <div className="it-runbar">
          <span className="it-runbar-status">
            <span className={`it-dot ${isRunning ? "running" : hasResult ? (result.ok ? "ok" : "fail") : "idle"}`} />
            {isRunning
              ? <span>Running…</span>
              : hasResult
                ? <span><strong>{result.ok ? "Passed" : "Failed"}</strong> in {result.duration_ms ?? 0} ms</span>
                : <span>Ready</span>}
          </span>
          <div className="it-runbar-spacer" />
          {confirming ? (
            <>
              <button className="btn btn-outline" onClick={onCancel} disabled={isRunning}>Cancel</button>
              <button className="btn btn-danger" onClick={onConfirm} disabled={isRunning}>
                {isRunning ? "Running…" : "Confirm & run"}
              </button>
            </>
          ) : (
            <button className="btn btn-primary" onClick={onRun} disabled={isRunning}>
              {isRunning ? "Running…" : hasResult ? "Run again" : "Run test"}
            </button>
          )}
        </div>

        {hasResult && <ResultBlock result={result} testName={test.name} />}
      </div>
    </>
  );
}

/* ═══ Request preview block ═════════════════════════════ */
function RequestPreview({ preview, error }) {
  if (error) {
    return (
      <div className="it-preview">
        <div className="it-preview-head">
          <span>Request preview</span>
          <span className="it-preview-badge fail">error</span>
        </div>
        <pre className="it-preview-body">{error}</pre>
      </div>
    );
  }
  if (!preview?.request) {
    return (
      <div className="it-preview">
        <div className="it-preview-head">
          <span>Request preview</span>
          <span className="it-preview-badge">loading…</span>
        </div>
        <pre className="it-preview-body it-preview-dim">—</pre>
      </div>
    );
  }
  return (
    <div className="it-preview">
      <div className="it-preview-head">
        <span>Request preview</span>
        <span className="it-preview-badge ok">what will be sent</span>
      </div>
      <pre className="it-preview-body">{JSON.stringify(preview.request, null, 2)}</pre>
    </div>
  );
}

/* ═══ Result / console block ═════════════════════════════ */
function ResultBlock({ result, testName }) {
  // Error-only (no steps)
  if (result.error && (!result.steps || result.steps.length === 0)) {
    return <pre className="it-error">{result.error}</pre>;
  }

  const steps = result.steps || [];
  return (
    <>
      <div className="it-console">
        <div className="it-console-head">
          <span className="it-console-title">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="4 17 10 11 4 5" />
              <line x1="12" y1="19" x2="20" y2="19" />
            </svg>
            output
          </span>
          <span className="it-console-total">{steps.length} step{steps.length === 1 ? "" : "s"}</span>
        </div>

        <div className="it-console-body">
          {steps.map((s, i) => (
            <div key={i} className={`it-step ${s.ok ? "ok" : "fail"}`}>
              <span className="it-step-num">{String(i + 1).padStart(2, "0")}</span>
              <span className="it-step-mark">{s.ok ? "✓" : "✕"}</span>
              <span className="it-step-text">
                <span className="it-step-name">{s.name}</span>
              </span>
              <span className="it-step-time">{s.ms ?? 0} ms</span>
              {s.detail != null && s.detail !== "" && (
                <pre className="it-step-detail">
                  {typeof s.detail === "string" ? s.detail : JSON.stringify(s.detail, null, 2)}
                </pre>
              )}
            </div>
          ))}
        </div>

        <div className="it-console-foot">
          <span className={`it-foot-result ${result.ok ? "ok" : "fail"}`}>
            <span className={`it-dot ${result.ok ? "ok" : "fail"}`} />
            {result.ok ? "All passed" : "Failed"}
          </span>
          <span className="it-foot-dur">{testName} · {result.duration_ms ?? 0} ms total</span>
        </div>
      </div>

      {result.summary && (
        <details className="it-summary">
          <summary>Summary payload</summary>
          <pre>{JSON.stringify(result.summary, null, 2)}</pre>
        </details>
      )}
    </>
  );
}
